import { isDefined } from '@sgme/fp';
import type { ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import type { IBlotterItem, IBlotterQuoteItem } from 'models/IBlotterItem';
import { useCallback } from 'react';
import { blotterPanelItemDetailsRequestedThunk } from 'state/blotters/blotterPanel/blotterPanel.thunks';
import {
  quoteBlotterColumnStateChanged,
  quoteBlotterFetchingFailed,
  quoteBlotterItemsPerPageChanged,
  quoteBlotterSetLoading,
} from 'state/blotters/quote/quoteBlotter.action';
import { getQuoteBlotterPreferenceState, getQuoteBlotterRequest } from 'state/blotters/quote/quoteBlotter.selector';
import { initFeedbackThunkOnSetActiveItemThunk } from 'state/blotters/quote/quoteBlotter.thunks';
import { getUserPreferencesLanguage } from 'state/selectors';
import { isPastDate } from 'utils/core/date';

export function useQuotesBlotterServerSide() {
  const isoLang = useAppSelector(state => getUserPreferencesLanguage(state));
  const blotterRequest = useAppSelector(state => getQuoteBlotterRequest(state.blotters.quotes, isoLang));
  const quotePreference = useAppSelector(state => getQuoteBlotterPreferenceState(state.blotters.quotes));
  const columnDefinitions = useAppSelector(state => state.blotters.quotes.columnDefinition);

  const isDateFilterCoherent =
    isDefined(blotterRequest.startDate) &&
    isDefined(blotterRequest.endDate) &&
    blotterRequest.startDate < blotterRequest.endDate;

  const dispatch = useThunkDispatch();

  const onBlotterColumnStateChanged = useCallback(
    (columnState: readonly ColumnPreferenceState[]) => {
      dispatch(quoteBlotterColumnStateChanged(columnState));
    },
    [dispatch],
  );

  const onSetActiveItem = useCallback(
    (item?: IBlotterQuoteItem) => {
      dispatch(blotterPanelItemDetailsRequestedThunk(item => item?.quoteId, item as IBlotterItem));
      dispatch(initFeedbackThunkOnSetActiveItemThunk(item));
    },
    [dispatch],
  );

  const onItemsReceived = useCallback(() => {
    dispatch(quoteBlotterSetLoading(false));
  }, [dispatch]);

  const onItemsRequested = useCallback(() => {
    dispatch(quoteBlotterSetLoading(true));
  }, [dispatch]);

  const onRequestFailed = useCallback(() => {
    dispatch(quoteBlotterFetchingFailed());
  }, [dispatch]);

  const onPageSizeChanged = useCallback(
    (pageSize: number) => {
      dispatch(quoteBlotterItemsPerPageChanged(pageSize));
    },
    [dispatch],
  );

  return {
    columnDefinitions,
    ...quotePreference,
    blotterRequest,
    isBlotterRefreshable: isDateFilterCoherent && isPastDate(blotterRequest.endDate) !== true,
    onBlotterColumnStateChanged,
    onSetActiveItem,
    onItemsReceived,
    onItemsRequested,
    onPageSizeChanged,
    onRequestFailed,
  };
}
